import { useState } from "react";
import { Link } from "react-router-dom";
import type { Product } from "@/mocks/products";
import { formatBRL } from "@/utils/format";

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
}

export default function ProductCard({ product, onAddToCart }: ProductCardProps) {
  const [favorite, setFavorite] = useState(false);

  return (
    <article className="group flex flex-col" data-product-shop>
      <div className="relative w-full overflow-hidden rounded-lg bg-background-100">
        <Link to={`/produto/${product.id}`} className="product-photo block aspect-[4/5] w-full" aria-label={product.name}>
          <img
            src={product.image}
            alt={product.name}
            className="h-full w-full object-cover object-top"
          />
        </Link>
        <button
          type="button"
          aria-label={favorite ? "Remover dos favoritos" : "Adicionar aos favoritos"}
          onClick={() => setFavorite((prev) => !prev)}
          className={`absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-background-50/90 backdrop-blur transition-colors ${
            favorite ? "text-accent-600" : "text-foreground-700 hover:text-accent-600"
          }`}
        >
          <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"} text-lg`}></i>
        </button>
      </div>

      <div className="flex flex-1 flex-col pt-3">
        <h3 className="text-sm font-medium leading-snug text-foreground-900">
          <Link to={`/produto/${product.id}`} className="block py-2.5 transition-colors hover:text-primary-700">
            {product.name}
          </Link>
        </h3>
        <p className="mt-1 flex items-baseline gap-2">
          <span className="font-heading text-lg font-semibold text-foreground-950">
            {formatBRL(product.price)}
          </span>
          {product.compareAtPrice && product.compareAtPrice > product.price && (
            <span className="text-xs text-foreground-400 line-through">
              {formatBRL(product.compareAtPrice)}
            </span>
          )}
        </p>
        <p className="text-xs text-foreground-500">
          em até {product.installments}x de {formatBRL(product.installmentValue)}
        </p>
        <div className="mt-auto" />
        <button
          type="button"
          disabled={product.inStock === false}
          onClick={() => onAddToCart(product)}
          className="mt-3 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-background-50 transition-colors hover:bg-primary-600"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-shopping-cart-2-line text-sm"></i>
          </span>
          {product.inStock === false ? "Indisponível" : <>Adicionar<span className="hidden sm:inline">&nbsp;ao carrinho</span></>}
        </button>
        <Link
          to={`/produto/${product.id}`}
          aria-label={`Ver detalhes de ${product.name}`}
          className="product-details-link"
        >
          Ver detalhes
        </Link>
      </div>
    </article>
  );
}
